"use client";

/**
 * ChartRaw — thin public wrapper that renders a raw ECharts `option`.
 *
 * The `<Chart>` wrapper builds its option from `data`/`columns`/`viz` (the
 * v1 ChartSpec shape). Some surfaces (the Report viewer) need full control
 * over the ECharts option — diverging bars, stacked three-colour sentiment,
 * word clouds — that don't map onto the 7-type builder. `ChartRaw` exposes
 * the same lazily-loaded ECharts runtime, so they reuse the code-split
 * dependency without re-implementing the Suspense/resize plumbing.
 */

import type { EChartsOption } from "echarts";
import * as React from "react";

import { Skeleton } from "../primitives/skeleton.js";
import { ChartImplLazy } from "./lazy.js";

export interface ChartRawProps {
  readonly option: EChartsOption;
  readonly className?: string;
  readonly ariaLabel?: string;
  readonly onEvents?: Record<string, (params: unknown) => void>;
}

export const ChartRaw: React.FC<ChartRawProps> = ({
  option,
  className,
  ariaLabel,
  onEvents,
}) => (
  <div
    role="img"
    aria-label={ariaLabel ?? "Grafico"}
    className={`h-full w-full ${className ?? ""}`}
  >
    <React.Suspense fallback={<Skeleton className="h-full w-full" />}>
      <ChartImplLazy option={option} onEvents={onEvents} />
    </React.Suspense>
  </div>
);

ChartRaw.displayName = "ChartRaw";
